import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Form } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';

import { useForm } from '../../hooks';

import styles from './ListMoveStep.module.scss';

const ListMoveStep = React.memo(
  ({ projectsToLists, defaultPath, onMove, onTransfer, onBack, onClose }) => {
    const [t] = useTranslation();

    const [path, handleFieldChange, setPath] = useForm(() => ({
      ...defaultPath,
      boardId: null,
    }));

    const selectedProject = useMemo(
      () => projectsToLists.find((project) => String(project.id) === String(path.projectId)) || null,
      [projectsToLists, path.projectId],
    );

    const availableBoards = useMemo(() => {
      if (!selectedProject) {
        return [];
      }

      return selectedProject.boards.filter(
        (board) => String(board.id) !== String(defaultPath.boardId),
      );
    }, [selectedProject, defaultPath.boardId]);

    const handleProjectIdChange = useCallback(
      (_, data) => {
        setPath((prevPath) => ({
          ...prevPath,
          projectId: data.value,
          boardId: null,
        }));
      },
      [setPath],
    );

    const handleBoardIdChange = useCallback(
      (event, data) => {
        handleFieldChange(event, data);
      },
      [handleFieldChange],
    );

    const selectedBoard = useMemo(() => {
      if (!path.boardId) {
        return null;
      }

      return availableBoards.find((board) => String(board.id) === String(path.boardId)) || null;
    }, [availableBoards, path.boardId]);

    const handleSubmit = useCallback(() => {
      if (!path.boardId || !selectedBoard) {
        return;
      }

      if (String(path.boardId) !== String(defaultPath.boardId)) {
        onTransfer(path.boardId);
      } else {
        onClose();
      }
    }, [defaultPath, onTransfer, onClose, path.boardId, selectedBoard]);

    const isBoardReady = !!selectedBoard;

    return (
      <>
        <Popup.Header onBack={onBack}>
          {t('common.moveList', {
            context: 'title',
          })}
        </Popup.Header>
        <Popup.Content>
          <Form onSubmit={handleSubmit}>
            <div className={styles.text}>{t('common.project')}</div>
            <Dropdown
              fluid
              selection
              name="projectId"
              options={projectsToLists.map((project) => ({
                text: project.name,
                value: project.id,
              }))}
              value={selectedProject && selectedProject.id}
              placeholder={
                projectsToLists.length === 0 ? t('common.noProjects') : t('common.selectProject')
              }
              disabled={projectsToLists.length === 0}
              className={styles.field}
              onChange={handleProjectIdChange}
            />
            {selectedProject && (
              <>
                <div className={styles.text}>{t('common.board')}</div>
                <Dropdown
                  fluid
                  selection
                  name="boardId"
                  options={availableBoards.map((board) => ({
                    text: board.name,
                    value: board.id,
                  }))}
                  value={path.boardId || null}
                  placeholder={
                    availableBoards.length === 0
                      ? t('common.noBoards')
                      : t('common.selectBoard')
                  }
                  disabled={availableBoards.length === 0}
                  className={styles.field}
                  onChange={handleBoardIdChange}
                />
              </>
            )}
            <Button positive content={t('action.move')} disabled={!isBoardReady} />
          </Form>
        </Popup.Content>
      </>
    );
  },
);

ListMoveStep.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  projectsToLists: PropTypes.array.isRequired,
  defaultPath: PropTypes.object.isRequired,
  /* eslint-enable react/forbid-prop-types */
  onMove: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

ListMoveStep.defaultProps = {
  onBack: undefined,
};

export default ListMoveStep;
